import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Mail,
  Users,
  Newspaper,
  LogOut,
  Menu,
  X,
  Eye,
  Trash2,
  Plus,
  Edit,
  Check,
  Rss,
  RefreshCw,
  ExternalLink,
  Download,
  Inbox,
  Send,
  Reply,
  MailOpen,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ArticlePlaceholder } from "@/components/ArticlePlaceholder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth helper
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() });
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-overview">
      <h1 className="text-2xl text-white mb-8">Dashboard Overview</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: "Total Inquiries", value: stats?.total_inquiries || 0, icon: Mail },
          { label: "Unread Messages", value: stats?.unread_inquiries || 0, icon: Eye, highlight: true },
          { label: "Newsletter Subscribers", value: stats?.total_subscribers || 0, icon: Users },
          { label: "News Articles", value: stats?.total_news || 0, icon: Newspaper },
          { label: "RSS Feeds", value: stats?.total_rss_feeds || 0, icon: Rss },
          { label: "Total Emails", value: stats?.total_emails || 0, icon: Inbox },
          { label: "Unread Emails", value: stats?.unread_emails || 0, icon: MailOpen, highlight: true },
        ].map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`glass-card p-6 ${stat.highlight && stat.value > 0 ? "border-amber-400/50" : ""}`}
          >
            <div className="flex items-center justify-between mb-4">
              <stat.icon className={`w-5 h-5 ${stat.highlight && stat.value > 0 ? "text-amber-400" : "text-slate-500"}`} />
            </div>
            <p className={`text-3xl font-bold ${stat.highlight && stat.value > 0 ? "text-amber-400" : "text-white"}`}>
              {stat.value}
            </p>
            <p className="text-slate-500 text-sm mt-1">{stat.label}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Contact Inquiries
const ContactInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API}/admin/inquiries`, { headers: getAuthHeaders() });
      setInquiries(response.data);
    } catch (error) {
      console.error("Failed to fetch inquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${id}/read`, {}, { headers: getAuthHeaders() });
      setInquiries(inquiries.map((inq) => (inq.id === id ? { ...inq, is_read: true } : inq)));
      toast.success("Marked as read");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inquiry?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers: getAuthHeaders() });
      setInquiries(inquiries.filter((inq) => inq.id !== id));
      setSelectedInquiry(null);
      toast.success("Inquiry deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-inquiries">
      <h1 className="text-2xl text-white mb-8">Contact Inquiries</h1>

      {inquiries.length === 0 ? (
        <div className="text-center py-16 glass-card">
          <Mail className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500">No inquiries yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* List */}
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            {inquiries.map((inquiry) => (
              <div
                key={inquiry.id}
                data-testid={`inquiry-${inquiry.id}`}
                onClick={() => setSelectedInquiry(inquiry)}
                className={`glass-card p-4 cursor-pointer transition-all ${
                  selectedInquiry?.id === inquiry.id
                    ? "border-amber-400/50"
                    : "hover:border-white/20"
                } ${!inquiry.is_read ? "border-l-2 border-l-amber-400" : ""}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">{inquiry.name}</span>
                  <span className="text-xs text-slate-500">{formatDate(inquiry.created_at)}</span>
                </div>
                <p className="text-slate-400 text-sm truncate">{inquiry.email}</p>
                <p className="text-slate-500 text-xs mt-1 truncate">{inquiry.message}</p>
              </div>
            ))}
          </div>

          {/* Detail */}
          {selectedInquiry && (
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <span className={`text-xs px-2 py-1 badge-${selectedInquiry.inquiry_type === "investor" ? "gold" : "biotech"}`}>
                  {selectedInquiry.inquiry_type}
                </span>
                <div className="flex items-center gap-2">
                  {!selectedInquiry.is_read && (
                    <button
                      onClick={() => markAsRead(selectedInquiry.id)}
                      className="p-2 text-slate-400 hover:text-amber-400 transition-colors"
                      title="Mark as read"
                    >
                      <Check size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => deleteInquiry(selectedInquiry.id)}
                    className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <h2 className="text-white text-xl mb-2">{selectedInquiry.name}</h2>
              <p className="text-amber-400 text-sm mb-1">{selectedInquiry.email}</p>
              {selectedInquiry.phone && (
                <p className="text-slate-400 text-sm mb-1">{selectedInquiry.phone}</p>
              )}
              {selectedInquiry.company && (
                <p className="text-slate-500 text-sm mb-4">{selectedInquiry.company}</p>
              )}

              <div className="border-t border-white/10 pt-4 mt-4">
                <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {selectedInquiry.message}
                </p>
              </div>

              <p className="text-slate-600 text-xs mt-6">{formatDate(selectedInquiry.created_at)}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Newsletter Subscribers
const NewsletterSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        const response = await axios.get(`${API}/admin/subscribers`, { headers: getAuthHeaders() });
        setSubscribers(response.data.filter((s) => s.is_active));
      } catch (error) {
        console.error("Failed to fetch subscribers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubscribers();
  }, []);

  const removeSubscriber = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, { headers: getAuthHeaders() });
      setSubscribers(subscribers.filter((s) => s.id !== id));
      toast.success("Subscriber removed");
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const exportToCSV = () => {
    if (subscribers.length === 0) {
      toast.error("No subscribers to export");
      return;
    }
    
    // Create CSV content
    const headers = ["Email", "Subscribed Date"];
    const rows = subscribers.map(sub => [
      sub.email,
      new Date(sub.subscribed_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `subscribers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${subscribers.length} subscribers to CSV`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-subscribers">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl text-white">Newsletter Subscribers</h1>
          <span className="text-slate-500 text-sm">{subscribers.length} subscribers</span>
        </div>
        {subscribers.length > 0 && (
          <button
            onClick={exportToCSV}
            data-testid="export-csv-btn"
            className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-[#08090D] px-4 py-2 text-sm font-medium transition-colors"
          >
            <Download size={16} />
            Export CSV
          </button>
        )}
      </div>

      {subscribers.length === 0 ? (
        <div className="text-center py-16 glass-card">
          <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500">No subscribers yet</p>
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <table className="luxury-table w-full">
            <thead>
              <tr>
                <th className="text-left p-4">Email</th>
                <th className="text-left p-4">Subscribed</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((sub) => (
                <tr key={sub.id} data-testid={`subscriber-${sub.id}`}>
                  <td className="p-4 text-white">{sub.email}</td>
                  <td className="p-4 text-slate-500">{formatDate(sub.subscribed_at)}</td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => removeSubscriber(sub.id)}
                      className="text-slate-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// News Management
const NewsManagement = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [viewingArticle, setViewingArticle] = useState(null);
  const [refetching, setRefetching] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    summary: "",
    content: "",
    category: "company",
    image_url: "",
    is_published: true,
  });
  const [newCategory, setNewCategory] = useState({ name: "", color: "slate" });

  // Get base URL for preview links
  const baseUrl = window.location.origin;

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/admin/news`, { headers: getAuthHeaders() });
      setArticles(response.data);
    } catch (error) {
      console.error("Failed to fetch articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/news-categories`, { headers: getAuthHeaders() });
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, []);

  const resetForm = () => {
    setFormData({
      title: "",
      summary: "",
      content: "",
      category: "company",
      image_url: "",
      is_published: true,
    });
    setEditingArticle(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await axios.put(`${API}/admin/news/${editingArticle.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/admin/news`, formData, { headers: getAuthHeaders() });
        toast.success("Article created");
      }
      fetchArticles();
      resetForm();
    } catch (error) {
      toast.error("Failed to save article");
    }
  };

  const editArticle = (article) => {
    setFormData({
      title: article.title,
      summary: article.summary,
      content: article.content,
      category: article.category,
      image_url: article.image_url || "",
      is_published: article.is_published,
    });
    setEditingArticle(article);
    setViewingArticle(null);
    setShowForm(true);
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await axios.delete(`${API}/admin/news/${id}`, { headers: getAuthHeaders() });
      setArticles(articles.filter((a) => a.id !== id));
      setViewingArticle(null);
      toast.success("Article deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const togglePublished = async (article) => {
    try {
      await axios.put(`${API}/admin/news/${article.id}`, {
        ...article,
        is_published: !article.is_published
      }, { headers: getAuthHeaders() });
      setArticles(articles.map(a => 
        a.id === article.id ? { ...a, is_published: !a.is_published } : a
      ));
      if (viewingArticle?.id === article.id) {
        setViewingArticle({ ...viewingArticle, is_published: !article.is_published });
      }
      toast.success(article.is_published ? "Article unpublished" : "Article published");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied to clipboard");
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim()) return;
    
    try {
      await axios.post(`${API}/admin/news-categories`, newCategory, { headers: getAuthHeaders() });
      toast.success("Category created");
      setNewCategory({ name: "", color: "slate" });
      setShowCategoryForm(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await axios.delete(`${API}/admin/news-categories/${categoryId}`, { headers: getAuthHeaders() });
      toast.success("Category deleted");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete category");
    }
  };

  const refetchContent = async (articleId, sourceUrl) => {
    if (!sourceUrl) {
      toast.error("No source URL available");
      return;
    }
    
    setRefetching(true);
    try {
      const response = await axios.post(`${API}/admin/news/${articleId}/refetch`, {}, { headers: getAuthHeaders() });
      toast.success(`Content fetched successfully (${response.data.content_length} characters)`);
      // Refresh the articles list and update the editing article
      await fetchArticles();
      // Reload the article being edited
      const updatedArticle = (await axios.get(`${API}/admin/news`, { headers: getAuthHeaders() })).data.find(a => a.id === articleId);
      if (updatedArticle) {
        setFormData({
          title: updatedArticle.title,
          summary: updatedArticle.summary,
          content: updatedArticle.content,
          category: updatedArticle.category,
          image_url: updatedArticle.image_url || "",
          is_published: updatedArticle.is_published,
        });
        setEditingArticle(updatedArticle);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to fetch content from source";
      toast.error(message);
    } finally {
      setRefetching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-news" className="max-w-full overflow-x-hidden w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="min-w-0">
          <h1 className="text-2xl text-white">News Management</h1>
          <p className="text-slate-500 text-sm mt-1">{articles.length} articles total</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setViewingArticle(null);
            setShowForm(!showForm);
          }}
          data-testid="add-news-btn"
          className="inline-flex items-center justify-center gap-2 bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-4 sm:px-6 py-3 transition-all duration-300 whitespace-nowrap flex-shrink-0"
        >
          <Plus size={14} />
          New Article
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="glass-card p-4 sm:p-6 mb-8 overflow-hidden"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h2 className="text-white text-lg">{editingArticle ? "Edit Article" : "Create New Article"}</h2>
            {editingArticle && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {editingArticle.source_url && (
                  <a
                    href={editingArticle.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-amber-400 hover:underline"
                  >
                    <ExternalLink size={12} />
                    Original Source
                  </a>
                )}
                {editingArticle.is_published && (
                  <a
                    href={`${baseUrl}/news`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-green-400 hover:underline"
                  >
                    <Eye size={12} />
                    Preview on Site
                  </a>
                )}
              </div>
            )}
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="news-form">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">Title *</label>
                <input
                  type="text"
                  data-testid="news-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="luxury-input w-full"
                  placeholder="Article title"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-slate-500 text-xs uppercase tracking-wider">Category</label>
                  <button
                    type="button"
                    onClick={() => setShowCategoryForm(!showCategoryForm)}
                    className="text-amber-400 text-xs hover:underline"
                  >
                    + New Category
                  </button>
                </div>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="luxury-input w-full bg-transparent"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.slug} className="bg-[#0F172A]">
                      {cat.name}
                    </option>
                  ))}
                </select>
                
                {/* New Category Form */}
                {showCategoryForm && (
                  <div className="mt-3 p-3 bg-slate-800/50 rounded space-y-3">
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      placeholder="Category name"
                      className="luxury-input w-full text-sm"
                      data-testid="new-category-name-input"
                    />
                    <div className="flex items-center gap-2">
                      <select
                        value={newCategory.color}
                        onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                        className="luxury-input flex-1 bg-transparent text-sm"
                      >
                        <option value="slate" className="bg-[#0F172A]">Gray</option>
                        <option value="amber" className="bg-[#0F172A]">Gold</option>
                        <option value="blue" className="bg-[#0F172A]">Blue</option>
                        <option value="green" className="bg-[#0F172A]">Green</option>
                        <option value="red" className="bg-[#0F172A]">Red</option>
                        <option value="purple" className="bg-[#0F172A]">Purple</option>
                      </select>
                      <button
                        type="button"
                        onClick={handleCreateCategory}
                        data-testid="create-category-btn"
                        className="bg-amber-500 text-[#08090D] px-3 py-2 text-xs font-bold"
                      >
                        Add
                      </button>
                    </div>
                    
                    {/* Existing Categories */}
                    {categories.length > 0 && (
                      <div className="pt-2 border-t border-white/10">
                        <p className="text-slate-500 text-xs mb-2">Existing categories:</p>
                        <div className="flex flex-wrap gap-2">
                          {categories.map((cat) => (
                            <span key={cat.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded">
                              {cat.name}
                              {!["company", "investment", "market"].includes(cat.slug) && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteCategory(cat.id)}
                                  className="text-red-400 hover:text-red-300 ml-1"
                                  title="Delete category"
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">Summary *</label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                required
                rows={2}
                className="luxury-input w-full resize-none"
                placeholder="Brief summary for article preview"
              />
            </div>

            <div>
              <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">
                Content * <span className="text-slate-600 normal-case">(HTML supported)</span>
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
                rows={15}
                className="luxury-input w-full resize-y font-mono text-xs sm:text-sm leading-relaxed overflow-x-auto"
                style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
                placeholder="Full article content. HTML tags are supported for formatting."
              />
            </div>

            <div>
              <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">Featured Image URL</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="luxury-input w-full"
                placeholder="https://example.com/image.jpg"
              />
              {formData.image_url && (
                <div className="mt-3">
                  <img 
                    src={formData.image_url} 
                    alt="Preview" 
                    className="max-h-32 max-w-full object-cover rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            {/* Source & Preview Links (for existing articles) */}
            {editingArticle && (
              <div className="p-3 sm:p-4 bg-slate-800/50 rounded space-y-3 overflow-hidden">
                <div className="flex items-center justify-between">
                  <p className="text-slate-400 text-xs uppercase tracking-wider">Article Links</p>
                  {editingArticle.source_url && (
                    <button
                      type="button"
                      onClick={() => refetchContent(editingArticle.id, editingArticle.source_url)}
                      disabled={refetching}
                      className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 disabled:opacity-50"
                    >
                      <RefreshCw size={12} className={refetching ? "animate-spin" : ""} />
                      {refetching ? "Fetching..." : "Refetch Full Content"}
                    </button>
                  )}
                </div>
                
                {editingArticle.source_url && (
                  <div className="space-y-1">
                    <span className="text-slate-500 text-xs">Source URL:</span>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <input
                          type="text"
                          readOnly
                          value={editingArticle.source_url}
                          className="luxury-input w-full text-xs bg-transparent"
                          style={{ textOverflow: 'ellipsis' }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(editingArticle.source_url)}
                        className="p-2 text-slate-400 hover:text-amber-400 flex-shrink-0"
                        title="Copy link"
                      >
                        <ExternalLink size={14} />
                      </button>
                      <a
                        href={editingArticle.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-slate-400 hover:text-amber-400 flex-shrink-0"
                        title="Open source"
                      >
                        <Eye size={14} />
                      </a>
                    </div>
                  </div>
                )}
                
                <div className="space-y-1">
                  <span className="text-slate-500 text-xs">Preview URL:</span>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <input
                        type="text"
                        readOnly
                        value={`${baseUrl}/news`}
                        className="luxury-input w-full text-xs bg-transparent"
                        style={{ textOverflow: 'ellipsis' }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(`${baseUrl}/news`)}
                      className="p-2 text-slate-400 hover:text-amber-400 flex-shrink-0"
                      title="Copy link"
                    >
                      <ExternalLink size={14} />
                    </button>
                    <a
                      href={`${baseUrl}/news`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-green-400 flex-shrink-0"
                      title="Preview on site"
                    >
                      <Eye size={14} />
                    </a>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="published" className="text-slate-400 text-sm">Publish immediately</label>
            </div>

            <div className="flex flex-wrap gap-4 pt-4 border-t border-white/10">
              <button
                type="submit"
                className="bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-6 py-3 transition-all duration-300"
              >
                {editingArticle ? "Save Changes" : "Create Article"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="text-slate-400 hover:text-white text-sm uppercase tracking-wider"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Article Viewer */}
      {viewingArticle && !showForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass-card mb-8 overflow-hidden"
        >
          <div className="aspect-[21/9] max-h-64 overflow-hidden">
            {viewingArticle.image_url ? (
              <img
                src={viewingArticle.image_url}
                alt={viewingArticle.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <ArticlePlaceholder className={`w-full h-full ${viewingArticle.image_url ? 'hidden' : ''}`} />
          </div>
          <div className="p-4 sm:p-6">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
              <span className={`text-xs px-2 py-1 ${viewingArticle.is_published ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
                {viewingArticle.is_published ? 'Published' : 'Draft'}
              </span>
              <span className="text-slate-500 text-xs capitalize">{viewingArticle.category}</span>
              <span className="text-slate-600 text-xs">{formatDate(viewingArticle.published_at)}</span>
            </div>
            
            <h2 className="text-white text-xl sm:text-2xl mb-4 break-words">{viewingArticle.title}</h2>
            <p className="text-slate-400 text-sm mb-6 italic break-words">{viewingArticle.summary}</p>
            
            {/* Source & Preview Links */}
            <div className="flex flex-wrap items-center gap-4 mb-6 p-3 bg-slate-800/50 rounded text-xs">
              {viewingArticle.source_url && (
                <a 
                  href={viewingArticle.source_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-1 text-amber-400 hover:underline"
                >
                  <ExternalLink size={12} />
                  View Original Source
                </a>
              )}
              {viewingArticle.is_published && (
                <a 
                  href={`${baseUrl}/news`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-1 text-green-400 hover:underline"
                >
                  <Eye size={12} />
                  Preview on Website
                </a>
              )}
            </div>
            
            {/* Rich text content */}
            <div 
              className="prose prose-invert prose-sm max-w-none text-slate-300 leading-relaxed break-words
                prose-headings:text-white prose-headings:font-medium
                prose-a:text-amber-400 prose-a:no-underline hover:prose-a:underline prose-a:break-all
                prose-strong:text-white prose-em:text-slate-200
                prose-ul:list-disc prose-ol:list-decimal
                prose-blockquote:border-l-amber-400 prose-blockquote:text-slate-400
                prose-pre:whitespace-pre-wrap prose-pre:break-words
                prose-code:break-all"
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: viewingArticle.content }}
            />

            <div className="flex flex-wrap items-center gap-3 mt-6 pt-6 border-t border-white/10">
              <button
                onClick={() => editArticle(viewingArticle)}
                className="inline-flex items-center gap-2 bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-4 py-2"
              >
                <Edit size={14} />
                Edit
              </button>
              <button
                onClick={() => togglePublished(viewingArticle)}
                className="inline-flex items-center gap-2 border border-slate-600 text-slate-400 hover:text-white hover:border-white uppercase tracking-widest text-xs px-4 py-2"
              >
                {viewingArticle.is_published ? 'Unpublish' : 'Publish'}
              </button>
              <button
                onClick={() => deleteArticle(viewingArticle.id)}
                className="inline-flex items-center gap-2 border border-red-900 text-red-400 hover:bg-red-900/20 uppercase tracking-widest text-xs px-4 py-2"
              >
                <Trash2 size={14} />
                Delete
              </button>
              <button
                onClick={() => setViewingArticle(null)}
                className="ml-auto text-slate-500 hover:text-white text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Articles List */}
      {articles.length === 0 ? (
        <div className="text-center py-16 glass-card">
          <Newspaper className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500">No articles yet</p>
        </div>
      ) : (
        <div className="space-y-3 max-w-4xl">
          {articles.map((article) => (
            <div 
              key={article.id} 
              data-testid={`article-${article.id}`} 
              className={`glass-card p-4 cursor-pointer hover:border-amber-400/30 transition-all border ${viewingArticle?.id === article.id ? 'border-amber-400/50' : 'border-white/10'}`}
              onClick={() => { setViewingArticle(article); setShowForm(false); }}
            >
              <div className="flex items-start gap-4">
                {/* Thumbnail */}
                {article.image_url && (
                  <div className="w-20 h-14 flex-shrink-0 overflow-hidden rounded">
                    <img
                      src={article.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${article.is_published ? 'bg-green-400' : 'bg-slate-500'}`} />
                    <span className="text-slate-500 text-xs capitalize">{article.category}</span>
                    <span className="text-slate-600 text-xs">{formatDate(article.published_at)}</span>
                    {article.source_feed_id && (
                      <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400">RSS</span>
                    )}
                  </div>
                  <h3 className="text-white font-medium truncate">{article.title}</h3>
                  <p className="text-slate-500 text-sm truncate mt-1">{article.summary}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => { setViewingArticle(article); setShowForm(false); }}
                    className="p-2 text-slate-400 hover:text-white transition-colors"
                    title="View"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => editArticle(article)}
                    className="p-2 text-slate-400 hover:text-amber-400 transition-colors"
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => deleteArticle(article.id)}
                    className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// RSS Feeds Management
const RSSFeedsManagement = () => {
  const [feeds, setFeeds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);
  const [fetchingFeed, setFetchingFeed] = useState(null);
  const [fetchingAll, setFetchingAll] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    category: "market",
    is_active: true,
  });

  const fetchFeeds = async () => {
    try {
      const response = await axios.get(`${API}/admin/rss-feeds`, { headers: getAuthHeaders() });
      setFeeds(response.data);
    } catch (error) {
      console.error("Failed to fetch RSS feeds:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/news-categories`, { headers: getAuthHeaders() });
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  useEffect(() => {
    fetchFeeds();
    fetchCategories();
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      url: "",
      category: "market",
      is_active: true,
    });
    setEditingFeed(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFeed) {
        await axios.put(`${API}/admin/rss-feeds/${editingFeed.id}`, formData, { headers: getAuthHeaders() });
        toast.success("RSS feed updated");
      } else {
        await axios.post(`${API}/admin/rss-feeds`, formData, { headers: getAuthHeaders() });
        toast.success("RSS feed added");
      }
      fetchFeeds();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save RSS feed");
    }
  };

  const editFeed = (feed) => {
    setFormData({
      name: feed.name,
      url: feed.url,
      category: feed.category,
      is_active: feed.is_active,
    });
    setEditingFeed(feed);
    setShowForm(true);
  };

  const deleteFeed = async (id) => {
    if (!window.confirm("Delete this RSS feed?")) return;
    try {
      await axios.delete(`${API}/admin/rss-feeds/${id}`, { headers: getAuthHeaders() });
      setFeeds(feeds.filter((f) => f.id !== id));
      toast.success("RSS feed deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const fetchFeedArticles = async (feedId) => {
    setFetchingFeed(feedId);
    try {
      const response = await axios.post(`${API}/admin/rss-feeds/${feedId}/fetch`, {}, { headers: getAuthHeaders() });
      const result = response.data;
      if (result.articles_imported > 0) {
        toast.success(`Imported ${result.articles_imported} new articles from ${result.feed_name}`);
      } else if (result.errors.length > 0) {
        toast.error(result.errors[0]);
      } else {
        toast.info("No new articles to import");
      }
      fetchFeeds();
    } catch (error) {
      toast.error("Failed to fetch articles");
    } finally {
      setFetchingFeed(null);
    }
  };

  const fetchAllFeeds = async () => {
    setFetchingAll(true);
    try {
      const response = await axios.post(`${API}/admin/rss-feeds/fetch-all`, {}, { headers: getAuthHeaders() });
      const results = response.data;
      const totalImported = results.reduce((sum, r) => sum + r.articles_imported, 0);
      if (totalImported > 0) {
        toast.success(`Imported ${totalImported} new articles from ${results.length} feeds`);
      } else {
        toast.info("No new articles to import");
      }
      fetchFeeds();
    } catch (error) {
      toast.error("Failed to fetch articles");
    } finally {
      setFetchingAll(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-rss-feeds">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl text-white">RSS Feeds</h1>
        <div className="flex items-center gap-3">
          {feeds.length > 0 && (
            <button
              onClick={fetchAllFeeds}
              disabled={fetchingAll}
              data-testid="fetch-all-feeds-btn"
              className="inline-flex items-center gap-2 bg-transparent border border-slate-700 text-white hover:border-amber-400/50 hover:text-amber-400 uppercase tracking-widest text-xs px-4 py-3 transition-all duration-300 disabled:opacity-50"
            >
              <Download size={14} className={fetchingAll ? "animate-bounce" : ""} />
              {fetchingAll ? "Fetching..." : "Fetch All"}
            </button>
          )}
          <button
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
            data-testid="add-rss-feed-btn"
            className="inline-flex items-center gap-2 bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-6 py-3 transition-all duration-300"
          >
            <Plus size={14} />
            Add Feed
          </button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="glass-card p-6 mb-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="rss-feed-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">Feed Name</label>
                <input
                  type="text"
                  data-testid="rss-feed-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Yahoo Finance"
                  className="luxury-input w-full"
                />
              </div>
              <div>
                <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="luxury-input w-full bg-transparent"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.slug} className="bg-[#0F172A]">
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">RSS Feed URL</label>
              <input
                type="url"
                data-testid="rss-feed-url-input"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                required
                placeholder="https://example.com/feed.xml"
                className="luxury-input w-full"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="rss-active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="rss-active" className="text-slate-400 text-sm">Active (will be included in fetch all)</label>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-6 py-3 transition-all duration-300"
              >
                {editingFeed ? "Update" : "Add"} Feed
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="text-slate-400 hover:text-white text-sm uppercase tracking-wider"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Feeds List */}
      {feeds.length === 0 ? (
        <div className="text-center py-16 glass-card">
          <Rss className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500 mb-4">No RSS feeds configured</p>
          <p className="text-slate-600 text-sm">Add RSS feeds to automatically import news articles</p>
        </div>
      ) : (
        <div className="space-y-4">
          {feeds.map((feed) => (
            <div key={feed.id} data-testid={`rss-feed-${feed.id}`} className="glass-card p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-white font-medium">{feed.name}</h3>
                    <span className={`text-xs px-2 py-0.5 ${feed.is_active ? "bg-green-500/20 text-green-400" : "bg-slate-700 text-slate-400"}`}>
                      {feed.is_active ? "Active" : "Inactive"}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-400 capitalize">{feed.category}</span>
                  </div>
                  <a
                    href={feed.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 text-sm hover:text-amber-400 transition-colors flex items-center gap-1 truncate"
                  >
                    {feed.url}
                    <ExternalLink size={12} />
                  </a>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-600">
                    <span>Last fetched: {formatDate(feed.last_fetched)}</span>
                    <span>Articles imported: {feed.articles_imported || 0}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchFeedArticles(feed.id)}
                    disabled={fetchingFeed === feed.id}
                    className="p-2 text-slate-400 hover:text-amber-400 transition-colors disabled:opacity-50"
                    title="Fetch articles"
                  >
                    <RefreshCw size={16} className={fetchingFeed === feed.id ? "animate-spin" : ""} />
                  </button>
                  <button
                    onClick={() => editFeed(feed)}
                    className="p-2 text-slate-400 hover:text-amber-400 transition-colors"
                    title="Edit feed"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => deleteFeed(feed.id)}
                    className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                    title="Delete feed"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Help text */}
      <div className="mt-8 p-4 border border-white/10 rounded">
        <p className="text-slate-500 text-sm mb-2">Suggested RSS feeds for financial news:</p>
        <ul className="text-slate-600 text-xs space-y-1">
          <li>• Yahoo Finance: https://feeds.finance.yahoo.com/rss/2.0/headline?s=LEXX</li>
          <li>• MarketWatch: https://feeds.marketwatch.com/marketwatch/topstories/</li>
          <li>• Seeking Alpha: https://seekingalpha.com/market_currents.xml</li>
          <li>• Bloomberg: https://feeds.bloomberg.com/markets/news.rss</li>
        </ul>
      </div>
    </div>
  );
};

// Mailbox (Email Inbox)
const Mailbox = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [filter, setFilter] = useState("all"); // all, inbound, outbound
  const [emailSettings, setEmailSettings] = useState(null);
  const [composeData, setComposeData] = useState({
    to: "",
    subject: "",
    body: "",
    is_html: false,
  });
  const [sending, setSending] = useState(false);
  const [testingIncoming, setTestingIncoming] = useState(false);

  const fetchEmails = async () => {
    try {
      const params = filter !== "all" ? `?direction=${filter}` : "";
      const response = await axios.get(`${API}/admin/emails${params}`, { headers: getAuthHeaders() });
      setEmails(response.data);
    } catch (error) {
      console.error("Failed to fetch emails:", error);
      toast.error("Failed to load emails");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/emails/settings`, { headers: getAuthHeaders() });
      setEmailSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch email settings:", error);
    }
  };

  useEffect(() => {
    fetchEmails();
    fetchEmailSettings();
  }, [filter]);

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!composeData.to || !composeData.subject || !composeData.body) {
      toast.error("Please fill in all fields");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/admin/emails/send`, composeData, { headers: getAuthHeaders() });
      toast.success("Email sent successfully");
      setShowCompose(false);
      setComposeData({ to: "", subject: "", body: "", is_html: false });
      fetchEmails();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send email";
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const handleTestIncomingEmail = async () => {
    setTestingIncoming(true);
    try {
      await axios.post(`${API}/admin/emails/test-incoming`, {
        from_email: "investor@example.com",
        subject: "Investment Inquiry",
        body: "Hello,\n\nI am interested in learning more about your investment opportunities in micro-cap companies.\n\nPlease contact me at your earliest convenience.\n\nBest regards,\nJohn Doe"
      }, { headers: getAuthHeaders() });
      toast.success("Test incoming email created");
      fetchEmails();
    } catch (error) {
      toast.error("Failed to create test email");
    } finally {
      setTestingIncoming(false);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm("Delete this email?")) return;
    try {
      await axios.delete(`${API}/admin/emails/${emailId}`, { headers: getAuthHeaders() });
      setEmails(emails.filter((e) => e.id !== emailId));
      if (selectedEmail?.id === emailId) setSelectedEmail(null);
      toast.success("Email deleted");
    } catch (error) {
      toast.error("Failed to delete email");
    }
  };

  const handleReply = (email) => {
    setComposeData({
      to: email.from_email,
      subject: email.subject.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
      body: `\n\n---\nOn ${new Date(email.created_at).toLocaleString()}, ${email.from_email} wrote:\n${email.body_text || email.body_html || ""}`,
      is_html: false,
    });
    setShowCompose(true);
    setSelectedEmail(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-mailbox">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl text-white">Email Inbox</h1>
          <p className="text-slate-500 text-sm mt-1">{emails.length} emails</p>
        </div>
        <button
          onClick={() => {
            setShowCompose(!showCompose);
            setSelectedEmail(null);
            if (!showCompose) setComposeData({ to: "", subject: "", body: "", is_html: false });
          }}
          data-testid="compose-email-btn"
          className="inline-flex items-center justify-center gap-2 bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-4 sm:px-6 py-3 transition-all duration-300 whitespace-nowrap flex-shrink-0"
        >
          <Plus size={14} />
          Compose
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: "all", label: "All" },
          { key: "inbound", label: "Received" },
          { key: "outbound", label: "Sent" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 text-xs uppercase tracking-wider transition-all ${
              filter === tab.key
                ? "bg-amber-500 text-[#08090D] font-bold"
                : "bg-transparent border border-slate-700 text-slate-400 hover:border-amber-400/50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Compose Form */}
      {showCompose && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="glass-card p-4 sm:p-6 mb-8 overflow-hidden"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-white text-lg flex items-center gap-2">
              <Send size={18} />
              Compose Email
            </h2>
            <button
              onClick={() => setShowCompose(false)}
              className="text-slate-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSendEmail} className="space-y-4" data-testid="compose-form">
            <div>
              <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">To *</label>
              <input
                type="email"
                data-testid="email-to-input"
                value={composeData.to}
                onChange={(e) => setComposeData({ ...composeData, to: e.target.value })}
                required
                className="luxury-input w-full"
                placeholder="recipient@example.com"
              />
            </div>

            <div>
              <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">Subject *</label>
              <input
                type="text"
                data-testid="email-subject-input"
                value={composeData.subject}
                onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                required
                className="luxury-input w-full"
                placeholder="Email subject"
              />
            </div>

            <div>
              <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">Message *</label>
              <textarea
                data-testid="email-body-input"
                value={composeData.body}
                onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                required
                rows={8}
                className="luxury-input w-full resize-y"
                placeholder="Write your message here..."
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is-html"
                checked={composeData.is_html}
                onChange={(e) => setComposeData({ ...composeData, is_html: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="is-html" className="text-slate-400 text-sm">Send as HTML</label>
            </div>

            <div className="flex gap-4 pt-4 border-t border-white/10">
              <button
                type="submit"
                disabled={sending}
                data-testid="send-email-btn"
                className="inline-flex items-center gap-2 bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-6 py-3 transition-all duration-300 disabled:opacity-50"
              >
                <Send size={14} />
                {sending ? "Sending..." : "Send Email"}
              </button>
              <button
                type="button"
                onClick={() => setShowCompose(false)}
                className="text-slate-400 hover:text-white text-sm uppercase tracking-wider"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Email View */}
      {selectedEmail && !showCompose && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass-card p-4 sm:p-6 mb-8"
        >
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs px-2 py-1 ${
                  selectedEmail.direction === "inbound" 
                    ? "bg-blue-500/20 text-blue-400" 
                    : "bg-green-500/20 text-green-400"
                }`}>
                  {selectedEmail.direction === "inbound" ? "Received" : "Sent"}
                </span>
                <span className="text-slate-500 text-xs">{new Date(selectedEmail.created_at).toLocaleString()}</span>
              </div>
              <h2 className="text-white text-xl mb-2 break-words">{selectedEmail.subject}</h2>
              <p className="text-amber-400 text-sm">
                {selectedEmail.direction === "inbound" ? "From: " : "To: "}
                {selectedEmail.direction === "inbound" ? selectedEmail.from_email : selectedEmail.to_email}
              </p>
            </div>
            <button
              onClick={() => setSelectedEmail(null)}
              className="text-slate-400 hover:text-white ml-4"
            >
              <X size={20} />
            </button>
          </div>

          <div className="border-t border-white/10 pt-4">
            {selectedEmail.body_html ? (
              <div 
                className="prose prose-invert prose-sm max-w-none text-slate-300"
                dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }}
              />
            ) : (
              <p className="text-slate-300 whitespace-pre-wrap">{selectedEmail.body_text || "No content"}</p>
            )}
          </div>

          <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/10">
            {selectedEmail.direction === "inbound" && (
              <button
                onClick={() => handleReply(selectedEmail)}
                className="inline-flex items-center gap-2 bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-4 py-2"
              >
                <Reply size={14} />
                Reply
              </button>
            )}
            <button
              onClick={() => handleDeleteEmail(selectedEmail.id)}
              className="inline-flex items-center gap-2 border border-red-900 text-red-400 hover:bg-red-900/20 uppercase tracking-widest text-xs px-4 py-2"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        </motion.div>
      )}

      {/* Email List */}
      {emails.length === 0 ? (
        <div className="text-center py-16 glass-card">
          <Inbox className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500">No emails yet</p>
          <p className="text-slate-600 text-sm mt-2">
            {filter === "all" 
              ? "Send your first email or configure Mailgun webhooks to receive emails" 
              : filter === "inbound" 
                ? "No received emails" 
                : "No sent emails"}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {emails.map((email) => (
            <div
              key={email.id}
              data-testid={`email-${email.id}`}
              onClick={() => { setSelectedEmail(email); setShowCompose(false); }}
              className={`glass-card p-4 cursor-pointer transition-all border ${
                selectedEmail?.id === email.id
                  ? "border-amber-400/50"
                  : "hover:border-white/20 border-white/10"
              } ${!email.read && email.direction === "inbound" ? "border-l-2 border-l-amber-400" : ""}`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  email.direction === "inbound" ? "bg-blue-500/20" : "bg-green-500/20"
                }`}>
                  {email.direction === "inbound" ? (
                    <Inbox className="w-5 h-5 text-blue-400" />
                  ) : (
                    <Send className="w-5 h-5 text-green-400" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className={`text-sm font-medium truncate ${!email.read && email.direction === "inbound" ? "text-white" : "text-slate-300"}`}>
                      {email.direction === "inbound" ? email.from_email : email.to_email}
                    </span>
                    <span className="text-slate-500 text-xs flex-shrink-0">{formatDate(email.created_at)}</span>
                  </div>
                  <p className={`text-sm truncate ${!email.read && email.direction === "inbound" ? "text-slate-200 font-medium" : "text-slate-400"}`}>
                    {email.subject}
                  </p>
                  <p className="text-slate-500 text-xs mt-1 truncate">
                    {email.body_text?.substring(0, 100) || email.body_html?.replace(/<[^>]+>/g, '').substring(0, 100) || "No preview"}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleDeleteEmail(email.id)}
                    className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mailgun Setup Notice */}
      <div className="mt-8 p-4 border border-white/10 rounded space-y-4">
        <div>
          <p className="text-slate-400 text-sm mb-3">📧 Email Settings</p>
          {emailSettings && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-800/50 rounded">
                <span className="text-slate-500">Sender Name:</span>
                <p className="text-white mt-1">{emailSettings.from_name || 'Not set'}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded">
                <span className="text-slate-500">Sender Email:</span>
                <p className="text-amber-400 mt-1">{emailSettings.from_email}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded">
                <span className="text-slate-500">Domain:</span>
                <p className="text-white mt-1">{emailSettings.domain}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded">
                <span className="text-slate-500">Status:</span>
                <p className={`mt-1 ${emailSettings.configured ? 'text-green-400' : 'text-red-400'}`}>
                  {emailSettings.configured ? '✓ Configured' : '✗ Not Configured'}
                </p>
              </div>
            </div>
          )}
        </div>
        
        <div className="border-t border-white/10 pt-4">
          <p className="text-slate-500 text-sm mb-2">Receiving Emails</p>
          <ul className="text-slate-600 text-xs space-y-1 mb-3">
            <li>• To receive emails, configure a Mailgun route to forward to your webhook endpoint:</li>
            <li className="pl-4 text-amber-400/70">POST {window.location.origin}/api/webhooks/mailgun/incoming</li>
            <li>• Make sure your domain DNS is properly configured with Mailgun</li>
          </ul>
          <button
            onClick={handleTestIncomingEmail}
            disabled={testingIncoming}
            className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-slate-700 text-slate-400 hover:border-amber-400/50 hover:text-amber-400 transition-colors disabled:opacity-50"
          >
            <Inbox size={14} />
            {testingIncoming ? "Creating..." : "Simulate Incoming Email"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const navItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/inquiries", label: "Inquiries", icon: Mail },
    { path: "/admin/mailbox", label: "Mailbox", icon: Inbox },
    { path: "/admin/subscribers", label: "Subscribers", icon: Users },
    { path: "/admin/news", label: "News", icon: Newspaper },
    { path: "/admin/rss-feeds", label: "RSS Feeds", icon: Rss },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`admin-sidebar fixed lg:static inset-y-0 left-0 z-40 w-64 transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 transition-transform duration-300 border-r border-white/5`}
      >
        <div className="p-6 border-b border-white/5">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 border border-amber-400/50 flex items-center justify-center">
              <span className="text-amber-400 font-bold">UV</span>
            </div>
            <span className="text-white text-sm font-light tracking-wider">Admin</span>
          </Link>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`admin-nav-item flex items-center gap-3 px-4 py-3 text-sm ${
                isActive(item.path) ? "active text-amber-400" : "text-slate-400"
              }`}
            >
              <item.icon size={18} />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/5">
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-3 px-4 py-3 text-slate-500 hover:text-red-400 transition-colors text-sm w-full"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-white/5">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white p-2"
          >
            <Menu size={24} />
          </button>
          <span className="text-white text-sm">Admin Dashboard</span>
          <div className="w-10" />
        </header>

        {/* Content */}
        <div className="p-4 sm:p-6 lg:p-12 overflow-x-hidden">
          <Routes>
            <Route path="dashboard" element={<DashboardOverview />} />
            <Route path="inquiries" element={<ContactInquiries />} />
            <Route path="mailbox" element={<Mailbox />} />
            <Route path="subscribers" element={<NewsletterSubscribers />} />
            <Route path="news" element={<NewsManagement />} />
            <Route path="rss-feeds" element={<RSSFeedsManagement />} />
            <Route path="*" element={<DashboardOverview />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
